import {
  SEEDANCE_STORAGE_KEYS,
  seedanceWorkspaceDb,
} from './seedanceStorage';

/** 任务列表 task_id → 字幕擦除 task_id */
export type EraseSubtitleTaskMap = Record<string, string>;

export interface EraseSubtitleTaskMappingMeta {
  eraseTaskId: string;
  videoUrl?: string;
  submittedAt: string;
}

export type EraseSubtitleTaskMapWithMeta = Record<string, EraseSubtitleTaskMappingMeta>;

function normalizeSourceTaskId(value: unknown): string | null {
  if (value == null) return null;
  const id = String(value).trim();
  return id.length > 0 ? id : null;
}

function normalizeEraseTaskId(value: unknown): string | null {
  return normalizeSourceTaskId(value);
}

export async function loadEraseSubtitleTaskMap(): Promise<EraseSubtitleTaskMap> {
  await seedanceWorkspaceDb.init();
  const raw = await seedanceWorkspaceDb.get<EraseSubtitleTaskMapWithMeta | EraseSubtitleTaskMap>(
    SEEDANCE_STORAGE_KEYS.eraseSubtitleTaskMap,
  );
  if (!raw || typeof raw !== 'object') return {};

  const map: EraseSubtitleTaskMap = {};
  for (const [sourceTaskId, value] of Object.entries(raw)) {
    const key = normalizeSourceTaskId(sourceTaskId);
    if (!key) continue;
    if (typeof value === 'string') {
      const eraseTaskId = normalizeEraseTaskId(value);
      if (eraseTaskId) map[key] = eraseTaskId;
      continue;
    }
    if (value && typeof value === 'object') {
      const eraseTaskId = normalizeEraseTaskId((value as EraseSubtitleTaskMappingMeta).eraseTaskId);
      if (eraseTaskId) map[key] = eraseTaskId;
    }
  }
  return map;
}

export async function loadEraseSubtitleTaskMapWithMeta(): Promise<EraseSubtitleTaskMapWithMeta> {
  await seedanceWorkspaceDb.init();
  const raw = await seedanceWorkspaceDb.get<EraseSubtitleTaskMapWithMeta | EraseSubtitleTaskMap>(
    SEEDANCE_STORAGE_KEYS.eraseSubtitleTaskMap,
  );
  if (!raw || typeof raw !== 'object') return {};

  const map: EraseSubtitleTaskMapWithMeta = {};
  for (const [sourceTaskId, value] of Object.entries(raw)) {
    const key = normalizeSourceTaskId(sourceTaskId);
    if (!key) continue;
    if (typeof value === 'string') {
      const eraseTaskId = normalizeEraseTaskId(value);
      if (eraseTaskId) {
        map[key] = { eraseTaskId, submittedAt: new Date(0).toISOString() };
      }
      continue;
    }
    if (value && typeof value === 'object') {
      const eraseTaskId = normalizeEraseTaskId((value as EraseSubtitleTaskMappingMeta).eraseTaskId);
      if (eraseTaskId) {
        map[key] = {
          eraseTaskId,
          videoUrl: typeof value.videoUrl === 'string' ? value.videoUrl : undefined,
          submittedAt:
            typeof value.submittedAt === 'string' && value.submittedAt.trim()
              ? value.submittedAt
              : new Date(0).toISOString(),
        };
      }
    }
  }
  return map;
}

export async function saveEraseSubtitleTaskMapping(
  sourceTaskId: string,
  eraseTaskId: string,
  meta?: Pick<EraseSubtitleTaskMappingMeta, 'videoUrl'>,
): Promise<void> {
  const sourceId = normalizeSourceTaskId(sourceTaskId);
  const eraseId = normalizeEraseTaskId(eraseTaskId);
  if (!sourceId || !eraseId) {
    throw new Error('无效的任务 ID');
  }

  await seedanceWorkspaceDb.init();
  if (!seedanceWorkspaceDb.db) throw new Error('IndexedDB 不可用');

  const current = await loadEraseSubtitleTaskMapWithMeta();
  current[sourceId] = {
    eraseTaskId: eraseId,
    videoUrl: meta?.videoUrl,
    submittedAt: new Date().toISOString(),
  };
  await seedanceWorkspaceDb.setAsync(SEEDANCE_STORAGE_KEYS.eraseSubtitleTaskMap, current);
}

export async function getEraseSubtitleTaskId(sourceTaskId: string): Promise<string | null> {
  const sourceId = normalizeSourceTaskId(sourceTaskId);
  if (!sourceId) return null;
  const map = await loadEraseSubtitleTaskMap();
  return map[sourceId] ?? null;
}

export async function removeEraseSubtitleTaskMapping(sourceTaskId: string): Promise<void> {
  const sourceId = normalizeSourceTaskId(sourceTaskId);
  if (!sourceId) return;

  await seedanceWorkspaceDb.init();
  if (!seedanceWorkspaceDb.db) return;

  const current = await loadEraseSubtitleTaskMapWithMeta();
  if (!(sourceId in current)) return;
  delete current[sourceId];
  await seedanceWorkspaceDb.setAsync(SEEDANCE_STORAGE_KEYS.eraseSubtitleTaskMap, current);
}
